"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { apiGetQuiet, apiPut, apiPost, setApiDocumentSet } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

/** Id of the virtual "All" set — a read-only view across every set. */
export const ALL_SET_ID = 0;

export interface DocumentSetInfo {
  id: number;
  name: string;
  /** Parent set id when this is a nested group; omitted/null at the top level. */
  parentId?: number | null;
  /** `Parent / Child` when nested, otherwise the same as `name`. */
  path?: string;
  /** False for "All": it can be browsed and searched, but not uploaded into. */
  uploadable?: boolean;
  /** Top-level container that can hold nested sets. Only groups get a circle-plus. */
  isGroup?: boolean;
}

export function setPath(set: DocumentSetInfo): string {
  return set.path?.trim() || set.name;
}

interface DocumentSetContextValue {
  sets: DocumentSetInfo[];
  /** Active set id, as a string (or null). */
  current: string | null;
  /** True when the active set is the virtual "All" view. */
  isAll: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
  selectSet: (id: number) => Promise<void>;
  createSet: (
    name: string,
    parentId?: number | null,
    isGroup?: boolean,
  ) => Promise<DocumentSetInfo>;
}

const DocumentSetContext = createContext<DocumentSetContextValue>({
  sets: [],
  current: null,
  isAll: false,
  loading: true,
  refresh: async () => {},
  selectSet: async () => {},
  createSet: async () => ({ id: ALL_SET_ID, name: "" }),
});

export function useDocumentSet() {
  return useContext(DocumentSetContext);
}

const LS_KEY = "qscanner.documentSet";

export function DocumentSetProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [sets, setSets] = useState<DocumentSetInfo[]>([]);
  const [current, setCurrent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user || user.mustChangePassword) {
      setSets([]);
      setCurrent(null);
      setApiDocumentSet(null);
      setLoading(false);
      return;
    }
    try {
      const res = await apiGetQuiet<{
        sets: DocumentSetInfo[];
        current: number | null;
      }>("/document-sets");
      const list = res.sets ?? [];
      setSets(list);

      let cur: string | null = res.current != null ? String(res.current) : null;
      // Drop selection if it's no longer one of the user's sets.
      if (cur && !list.some((s) => String(s.id) === cur)) {
        cur = null;
      }
      if (!cur) {
        const saved =
          typeof window !== "undefined" ? localStorage.getItem(LS_KEY) : null;
        if (saved && list.some((s) => String(s.id) === saved)) {
          await apiPut("/document-sets/current", { id: Number(saved) });
          cur = saved;
        }
      }
      setCurrent(cur);
      setApiDocumentSet(cur);
      if (cur) localStorage.setItem(LS_KEY, cur);
      else if (typeof window !== "undefined") localStorage.removeItem(LS_KEY);
    } catch {
      setSets([]);
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  const selectSet = useCallback(async (id: number) => {
    const res = await apiPut<{ ok: boolean; current: number }>(
      "/document-sets/current",
      { id },
    );
    const cur = String(res.current);
    setCurrent(cur);
    setApiDocumentSet(cur);
    localStorage.setItem(LS_KEY, cur);
  }, []);

  const createSet = useCallback(
    async (name: string, parentId?: number | null, isGroup?: boolean) => {
      const body: { name: string; parentId?: number; isGroup?: boolean } = { name };
      if (parentId != null && parentId > 0) body.parentId = parentId;
      if (isGroup) body.isGroup = true;
      const info = await apiPost<DocumentSetInfo>("/document-sets", body);
      await refresh();
      return info;
    },
    [refresh],
  );

  const isAll = current === String(ALL_SET_ID);

  return (
    <DocumentSetContext.Provider
      value={{ sets, current, isAll, loading, refresh, selectSet, createSet }}
    >
      {children}
    </DocumentSetContext.Provider>
  );
}
